import { FC, useState } from "react";
import {
	Box,
	LinearProgress,
	Pagination,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useUserStore } from "../../../../../stores/user.store";
import useEventActivityReport from "../../../../../hooks/useEventActivityReport";
import ReportFiltersBar, {
	ReportFilters,
} from "../../components/ReportFiltersBar";
import EventActivityKpis from "./EventActivityKpis";
import EventActivityPivotTable from "./EventActivityPivotTable";

interface Filters extends ReportFilters {
	pageNumber: number;
	pageSize: number;
}

interface EventActivityTabProps {
	programId: string;
}

const EventActivityTab: FC<EventActivityTabProps> = ({ programId }) => {
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";

	const [filters, setFilters] = useState<Filters>({
		orgUnitId: "",
		researcherId: "",
		startDate: null,
		endDate: null,
		pageNumber: 0,
		pageSize: 25,
	});

	const { data, isLoading, isFetching } = useEventActivityReport({
		accountId,
		programId,
		startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
		endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
		pageNumber: filters.pageNumber,
		pageSize: filters.pageSize,
	});

	const hasNoEvents = data && data.events.length === 0;
	const pageCount = data
		? Math.max(1, Math.ceil(data.pagination.total / data.pagination.pageSize))
		: 1;

	return (
		<Stack spacing={3}>
			<ReportFiltersBar
				idPrefix="event-activity"
				filters={filters}
				onChange={(next) =>
					setFilters((p) => ({ ...p, ...next, pageNumber: 0 }))
				}
				hiddenFilters={["orgUnit", "researcher"]}
			/>

			{(isLoading || isFetching) && <LinearProgress />}

			{hasNoEvents ? (
				<Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
					<Typography variant="h6" gutterBottom>
						No events scheduled for this program
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Configure events under the program&apos;s treatment plan
						(SmartProgram workflow, days 1–14) to start tracking activity.
					</Typography>
				</Paper>
			) : (
				<>
					<EventActivityKpis
						kpis={
							data?.kpis ?? {
								expected: 0,
								submitted: 0,
								missed: 0,
								pending: 0,
							}
						}
					/>

					<EventActivityPivotTable
						events={data?.events ?? []}
						rows={data?.rows ?? []}
					/>

					{data && data.pagination.total > data.pagination.pageSize && (
						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Pagination
								count={pageCount}
								page={filters.pageNumber + 1}
								onChange={(_e, p) =>
									setFilters((prev) => ({ ...prev, pageNumber: p - 1 }))
								}
								color="primary"
								size="small"
							/>
						</Box>
					)}
				</>
			)}
		</Stack>
	);
};

export default EventActivityTab;
